import { Component } from '@theme/component';

// A single frame reads every dirty title before applying any style changes.
const pending = new Set();
let frame;
function schedule(title) {
  pending.add(title);
  frame ??= requestAnimationFrame(() => {
    frame = undefined;
    const updates = [...pending].filter(title => title.isConnected).map(title => title.measureTruncation());
    pending.clear();
    for (const update of updates) {
      if (!update) continue;
      const { text, maxLines } = update;
      text.style.display = '-webkit-box';
      text.style.webkitBoxOrient = 'vertical';
      text.style.overflow = 'hidden';
      text.style.textOverflow = 'ellipsis';
      text.style.webkitLineClamp = String(maxLines);
    }
  });
}
const titles = new Set();
const observer = typeof ResizeObserver === 'function'
  ? new ResizeObserver(entries => entries.forEach(({ target }) => schedule(target)))
  : null;
const resize = () => titles.forEach(schedule);

/** Image-free product gallery title: preserve height-based truncation. */
class ProductTitle extends Component {
  connectedCallback() {
    super.connectedCallback();
    titles.add(this);
    if (observer) observer.observe(this);
    else if (titles.size === 1) window.addEventListener('resize', resize);
    schedule(this);
  }

  measureTruncation() {
    const text = this.refs.text || this.querySelector('.title-text') || this;
    if (!text.textContent) return;
    const height = this.clientHeight;
    const style = window.getComputedStyle(this);
    const lineHeight = parseFloat(style.lineHeight);
    if (!height || !Number.isFinite(lineHeight) || lineHeight <= 0) return;
    const available = height - (parseFloat(style.paddingTop) || 0) - (parseFloat(style.paddingBottom) || 0);
    return { text, maxLines: Math.max(1, Math.floor(available / lineHeight)) };
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    observer?.unobserve(this);
    titles.delete(this);
    pending.delete(this);
    if (!observer && !titles.size) window.removeEventListener('resize', resize);
    if (!pending.size && frame !== undefined) {
      cancelAnimationFrame(frame);
      frame = undefined;
    }
  }
}

if (!customElements.get('product-title')) customElements.define('product-title', ProductTitle);
export default ProductTitle;
